/**
 * useAuth Hook - DEPRECATED
 * ==========================
 * 
 * THIS HOOK IS DEPRECATED.
 * USE AuthContext from App.js instead:
 * 
 * import { useAuth } from '../App';
 * 
 * The main AuthProvider in App.js is the SINGLE SOURCE OF TRUTH for:
 * - user
 * - profile 
 * - session
 * - loading state
 * 
 * DO NOT use supabase.auth.getSession() or getUser() directly.
 * All auth state comes from AuthContext.
 */

import { supabase, isSupabaseConfigured } from '../lib/supabase';

// ============================================================================
// CACHED SESSION - For service files that can't use hooks
// This is populated by AuthProvider and used by services
// ============================================================================

let _cachedSession = null;
let _cachedUser = null;

/**
 * Set cached session (called by AuthProvider only)
 */
export function setCachedAuthState(session, user) {
  _cachedSession = session;
  _cachedUser = user;
}

/**
 * Get cached session (for service files)
 * NEVER calls supabase.auth directly - uses cached state from AuthProvider
 */
export function getCachedSession() {
  return _cachedSession;
}

/**
 * Get cached user (for service files)
 */
export function getCachedUser() {
  return _cachedUser;
}

/**
 * Get auth headers for API calls (uses cached session)
 * DOES NOT call supabase.auth - uses cached state
 */
export function getAuthHeadersSync() {
  if (_cachedSession?.access_token) {
    return {
      'Authorization': `Bearer ${_cachedSession.access_token}`,
      'Content-Type': 'application/json'
    };
  }
  return { 'Content-Type': 'application/json' };
}

/**
 * DEPRECATED: Get auth headers async
 * Use getAuthHeadersSync() instead
 */
export async function getAuthHeadersAsync() {
  // Use cached session, don't call supabase.auth
  return getAuthHeadersSync();
}

/**
 * DEPRECATED: Get Supabase session
 * Use getCachedSession() instead
 */
export async function getSupabaseSession() {
  console.warn('[useAuth] getSupabaseSession is DEPRECATED. Use getCachedSession() or useAuth() from App.js');
  return _cachedSession;
}

/**
 * Main useAuth hook - STUB that returns cached values
 * Real implementation is in App.js AuthProvider
 */
export function useAuth() {
  // This is a fallback - should use AuthContext from App.js
  console.warn('[useAuth] Using standalone hook. Prefer useAuth from App.js AuthContext');
  
  return {
    session: _cachedSession,
    user: _cachedUser,
    loading: false,
    error: null,
    isAuthenticated: !!_cachedUser,
    getAuthHeaders: getAuthHeadersSync,
    getAccessToken: () => _cachedSession?.access_token || null,
    signOut: async () => { if (supabase) await supabase.auth.signOut(); },
    refreshSession: async () => _cachedSession
  };
}

export default useAuth;
